import { opacify } from 'polished';
import { css } from 'styled-components';

import { AlertTypeEnum } from '../atoms/alert/alert.types';
import { palette } from '../variables/color-palette';

const typography = {
	fonts: {
		regular: 'ProximaNova-Regular',
		medium: 'ProximaNova-Medium',
		semibold: 'ProximaNova-SemiBold',
		bold: 'ProximaNova-Bold',
	},
};

const colors = {
	primary: {
		blue: palette.darkBlue[800],
		red: palette.red[500],
	},
	secondary: {
		gray: palette.gray[500],
		blue: palette.blue[500],
		green: palette.green[700],
		orange: palette.orange[700],
		purple: palette.purple[700],
	},
	text: {
		dark: palette.darkBlue[800],
		medium: palette.darkBlue[400],
		light: palette.gray[900],
		disabled: palette.gray[600],
		info: palette.blue[500],
		succes: palette.green[700],
		warning: palette.orange[700],
		error: palette.red[900],
		black: palette.black[900],
		white: palette.standard.white,
	},
	button: {
		background: {
			primary: palette.blue[500],
			secondaryDark: 'transparent',
			secondaryLight: 'transparent',
			success: palette.green[700],
			warning: palette.orange[700],
			error: palette.red[900],
			disabled: palette.gray[400],
			hover: {
				warning: palette.orange[900],
			},
		},
		border: {
			secondaryDark: palette.gray[600],
			secondaryDarkHover: palette.darkBlue[800],
			secondaryLight: palette.gray[600],
			secondaryLightHover: palette.standard.white,
		},
	},
	modal: {
		background: {
			default: palette.standard.white,
			gray: palette.gray[200],
		},
		header: {
			background: {
				default: palette.standard.white,
				blue: palette.darkBlue[800],
			},
		},
		icons: {
			gray: palette.gray[900],
			hover: palette.black[100],
			hoverWithTopBar: palette.standard.white,
		},
	},
	settingsPanel: {
		background: {
			gray: palette.gray[400],
			white: palette.standard.white,
		},
		icons: {
			gray: palette.gray[900],
		},
		labels: {
			block: palette.purple[700],
			column: palette.orange[700],
			regular: palette.blue[500],
			formFields: palette.standard.black,
			scrollDownButton: palette.standard.black,
			overlay: palette.red[400],
		},
	},
	thumbnail: {
		background: {
			gray: palette.gray[300],
		},
		border: {
			gray: palette.gray[400],
		},
	},
	icon: {
		blue: palette.blue[500],
		dark: palette.darkBlue[800],
		purple: palette.purple[700],
		gray: palette.gray[400],
		white: palette.standard.white,
	},
	tooltip: {
		background: { default: palette.standard.black },
		counterBadge: {
			background: {
				default: palette.green[700],
				warning: palette.orange[700],
				danger: palette.red[900],
			},
		},
	},
	radioButton: {
		disabled: palette.gray[400],
		checked: palette.blue[500],
		default: palette.gray[600],
		hover: palette.gray[900],
	},
	checkbox: {
		border: {
			disabled: palette.gray[400],
			checked: palette.blue[500],
			default: palette.gray[600],
			hover: palette.gray[900],
		},
		background: {
			disabled: palette.gray[400],
			checked: palette.blue[500],
			default: palette.standard.white,
		},
		icon: palette.standard.white,
	},
	switch: {
		default: palette.gray[500],
		checked: palette.blue[500],
		background: {
			disabled: palette.gray[400],
			checked: palette.blue[500],
			default: palette.gray[600],
			hover: palette.gray[900],
		},
		icon: palette.standard.white,
	},
	input: {
		text: { default: palette.darkBlue[800], disabled: palette.gray[600] },
		required: palette.red[800],
		border: {
			disabled: palette.gray[400],
			checked: palette.blue[500],
			default: palette.gray[600],
			focus: palette.blue[500],
			error: palette.red[500],
			success: palette.green[700],
		},
		background: {
			default: palette.standard.white,
			disabled: palette.gray[200],
		},
		placeholder: {
			default: palette.gray[900],
			disabled: palette.gray[600],
		},
		icon: {
			default: palette.gray[600],
			selected: palette.blue[500],
			success: palette.green[700],
		},
		tooltip: palette.gray[900],
	},
	rangeSlider: {
		background: {
			default: palette.gray[400],
			selected: palette.blue[500],
		},
		arrow: {
			color: { default: palette.darkBlue[800] },
		},
	},
	select: {
		arrow: {
			color: {
				default: palette.darkBlue[800],
				hover: palette.darkBlue[900],
				disabled: palette.gray[600],
			},
		},
		option: {
			background: {
				default: palette.standard.white,
				selected: palette.gray[300],
				disabled: palette.gray[400],
				hover: palette.gray[200],
			},
			templateStatus: {
				available: palette.green[700],
				unavailable: palette.red[500],
				draft: palette.gray[500],
			},
		},
		multiValue: {
			background: {
				default: palette.gray[400],
			},
			color: {
				default: palette.darkBlue[800],
			},
			clearIcon: {
				color: {
					default: palette.gray[800],
				},
			},
		},
	},
	alert: {
		background: {
			warning: palette.orange[100],
			danger: palette.red[100],
			note: palette.blue[100],
			tip: palette.green[100],
		},
		icon: {
			warning: palette.orange[700],
			danger: palette.red[500],
			note: palette.blue[500],
			tip: palette.green[700],
		},
	},
	snackbar: {
		icon: {
			warning: palette.orange[700],
			danger: palette.red[500],
			info: palette.blue[500],
			success: palette.green[700],
		},
	},
	editor: {
		spacing: {
			margin: opacify('-0.2', palette.red[500]),
			padding: opacify('-0.2', palette.green[700]),
		},
		dnd: {
			nodrop: palette.red[500],
		},
	},
	statusbar: {
		background: palette.gray[400],
		foreground: {
			default: palette.green[700],
			warning: palette.orange[700],
			danger: palette.red[900],
		},
	},
	statusoption: {
		draft: palette.gray[400],
		published: palette.green[700],
		offline: palette.red[900],
	},
	createTemplateIconCustomHover: {
		background: palette.green[700],
	},
	dropdown: {
		background: palette.standard.white,
		item: {
			background: {
				default: palette.standard.white,
				hover: palette.gray[200],
				selected: palette.gray[300],
				disabled: palette.gray[400],
			},
		},
		icon: {
			color: {
				default: palette.darkBlue[800],
				disabled: palette.gray[600],
			},
		},
	},
};

const sizes = {
	xs: 8,
	s: 12,
	m: 16,
	l: 24,
	xl: 30,
	xxl: 50,
	xxxl: 120,
};

const getAlertStyles = (alertType: AlertTypeEnum) => css`
	background-color: ${colors.alert.background[alertType]};
	border: 1px solid ${colors.alert.icon[alertType]};
	border-radius: 4px;
	.text {
		color: ${colors.text.dark};
	}

	.icon {
		color: ${colors.alert.icon[alertType]};
		min-height: 26px;
		min-width: 26px;
	}
`;

export const alertStyles = {
	warning: getAlertStyles(AlertTypeEnum.WARNING),
	danger: getAlertStyles(AlertTypeEnum.DANGER),
	note: getAlertStyles(AlertTypeEnum.NOTE),
	tip: getAlertStyles(AlertTypeEnum.TIP),
};

export const theme = {
	typography,
	colors,
	sizes,
	getAlertStyles,
};

export type Theme = typeof theme;
